import React from 'react';
import { Loader2, Calculator } from 'lucide-react';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { types, fmt$, isIncomplete, isDeclining, refusalText, NAVY_GRADIENT_STEEP, ACCENT_BAR_GRADIENT_SOFT } from './constants';
import { Button } from '../../components/ui/button';
import { IncomeTypeFields } from './IncomeTypeFields';
import { BRAND } from '../kit/tokens';

export function ManualCalculator({ type, setType, form, setForm, setNum, result, busy, calc, loanCtx, saveToLoan, savingToLoan, savedToLoan }) {
  const refused = isIncomplete(result);
  const missing = result?.missing_fields || [];
  const blank = missing.filter((m) => !isDeclining(m));
  const missingSet = new Set(missing.flatMap((m) => m.fields));
  const assumptions = result?.assumptions || [];
  // Saved only counts for the run on screen; a fresh Calculate re-enables the button.
  const savedThisRun = !!savedToLoan && savedToLoan.join() === (result?.calc_ids || [result?.calc_id]).join();
  return (
  <div>
    <div className="mb-4">
      <div className="text-[11px] uppercase tracking-widest font-bold text-brand-accent">Manual Override</div>
      <div className="font-serif text-lg font-semibold text-brand-navy">Fine-tune the calculation</div>
      <div className="text-xs text-slate-500 mt-0.5">Values auto-populated from your uploads. Edit any field and click Calculate to run again with any of the 5 program guidelines.</div>
    </div>

    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
      {types.map((t) => {
        const Icon = t.icon;
        return (
          <button key={t.k} onClick={() => setType(t.k)} data-testid={`income-type-${t.k}`}
            className="p-3 rounded-xl flex flex-col items-center gap-1.5 text-center transition"
            style={type === t.k ? { border: `2px solid ${BRAND.accent}`, background: '#F3FAE8' } : { border: '1px solid #e2e8f0', background: '#fff' }}>
            <Icon size={20} style={{ color: type === t.k ? BRAND.accent : '#64748b' }} />
            <span className="text-xs font-semibold text-brand-navy">{t.l}</span>
          </button>
        );
      })}
    </div>

    <div className="grid lg:grid-cols-2 gap-6">
      <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
        <div className="font-serif font-semibold mb-4 text-brand-navy">Inputs</div>
        <div className="space-y-4">
          <IncomeTypeFields type={type} form={form} setForm={setForm} setNum={setNum} missing={missingSet} />
          <div>
            <Label className="text-xs">Notes</Label>
            <Textarea rows={2} value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1" placeholder="Borrower name, YTD comment, etc." />
          </div>
          <Button onClick={calc} disabled={busy} data-testid="calculate-btn" variant="navy" className="w-full">
            {busy ? <><Loader2 size={16} className="animate-spin" /> Calculating...</> : <><Calculator size={16} /> Calculate</>}
          </Button>
        </div>
      </div>

      <div className="space-y-4">
        <div className="rounded-2xl p-6 text-white shadow-lg relative overflow-hidden" style={{ background: NAVY_GRADIENT_STEEP }}>
          <div className="absolute inset-x-0 top-0 h-[3px]" style={{ background: ACCENT_BAR_GRADIENT_SOFT }} />
          <div className="text-xs uppercase tracking-widest text-white/60">Qualifying Monthly Income</div>
          <div className="font-serif text-5xl font-bold mt-2 text-brand-accent-soft">{result && !refused ? fmt$(result.qualifying_monthly) : '—'}</div>
          {refused && (
            <div className="text-amber-300 text-sm mt-2 font-semibold" data-testid="calc-missing">
              {refusalText(result)}{blank.length > 0 && <> Enter {blank.length > 1 ? 'them' : 'it'} below and click Calculate.</>}
            </div>
          )}
          {result && !refused && <div className="text-white/60 text-sm mt-2">{result.method}</div>}
          {loanCtx && result && !refused && (
            <Button onClick={saveToLoan} disabled={savingToLoan || savedThisRun} variant="accent" size="sm" className="mt-4" data-testid="save-to-loan">
              {savingToLoan ? <><Loader2 size={14} className="animate-spin" /> Saving...</> : savedThisRun ? 'Saved to the loan file' : 'Save to loan file'}
            </Button>
          )}
        </div>
        {result?.breakdown?.length > 0 && (
          <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
            <div className="font-serif font-semibold mb-3 text-brand-navy">Breakdown</div>
            <div className="space-y-2">
              {result.breakdown.map((b, i) => (
                <div key={i} className="flex justify-between text-sm py-1.5 border-b border-slate-50 last:border-b-0">
                  <span className="text-slate-600">{b.label}</span>
                  <span className="font-semibold text-brand-navy">{typeof b.value === 'number' ? fmt$(b.value) : b.value}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {assumptions.length > 0 && (
          <div className="space-y-1 px-1" data-testid="calc-assumptions">
            {assumptions.map((a) => <div key={a.field} className="text-xs text-slate-500">{a.note}</div>)}
          </div>
        )}
      </div>
    </div>
  </div>
  );
}
